import { useMutation } from "@apollo/client";
import { ALLOWED_EXTENSIONS, MAX_FILE_SIZE } from "@constants/global";
import { Award } from "lucide-react";
import {
  DatePickerV2,
  Drawer,
  FileUploaderV2,
  type FileUploaderV2File,
  Input,
  ModalButtons,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/v2";

import { gqlUser } from "gql";
import {
  IAddUserCertificationInput,
  IAddUserCertificationRes,
  IUpdateUserCertificationInput,
  IUpdateUserCertificationRes,
} from "gql/User/mutations";
import moment from "moment";
import type { FormV2Props } from "components/ui/v2/form-v2-props";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { UploadPair } from "types/Multimedia.type";
import {
  ECertificationType,
  ETypeCertification,
  IUserCertification,
} from "types/User.types";
import { getQueryOperator } from "utils/helpers";

const emptyUploadPair: UploadPair = { name: "", base64: "" };

export const CERTIFICATION_FORM_ID = "certification-form";

interface ICertificationFormProps extends Pick<FormV2Props, "hideFooter" | "onFooterChange"> {
  certification?: IUserCertification;
  onClose?: () => void;
  open?: boolean;
  formId?: string;
}

const CertificationForm = ({
  certification,
  onClose,
  open,
  hideFooter = false,
  onFooterChange,
  formId = CERTIFICATION_FORM_ID,
}: ICertificationFormProps) => {
  const onFooterChangeRef = useRef(onFooterChange);
  onFooterChangeRef.current = onFooterChange;
  const { t } = useTranslation("common");

  const [file, setFile] = useState<UploadPair>(emptyUploadPair);
  const [certImage, setCertImage] = useState<FileUploaderV2File | null>(null);

  const [addUserCertification, { loading: addCertLoading }] = useMutation<
    { addUserCertification: IAddUserCertificationRes },
    { addUserCertificationInput: IAddUserCertificationInput }
  >(gqlUser.mutations.ADD_USER_CERTIFICATION, {
    refetchQueries: [getQueryOperator(gqlUser.queries.GET_MY_SPACE)],
    awaitRefetchQueries: true,
    onCompleted: () => {
      onClose?.();
    },
  });

  const [updateUserCertification, { loading: updateLoading }] = useMutation<
    { updateUserCertification: IUpdateUserCertificationRes },
    { updateUserCertificationInput: IUpdateUserCertificationInput }
  >(gqlUser.mutations.UPDATE_USER_CERTIFICATION, {
    refetchQueries: [getQueryOperator(gqlUser.queries.GET_MY_SPACE)],
    awaitRefetchQueries: true,
    onCompleted: () => {
      onClose?.();
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IAddUserCertificationInput>({
    defaultValues: {
      name: certification?.name || "",
      type: certification?.type || ECertificationType.OTHER,
      certificationUrl: certification?.certificationUrl || "",
      certificationType:
        certification?.certificationType || ETypeCertification.CERTIFICATION,
    },
  });

  const handleImageChange = (next: FileUploaderV2File | null) => {
    setCertImage(next);
    if (!next?.file) {
      setFile(emptyUploadPair);
      return;
    }
    if (next.file.size > MAX_FILE_SIZE) {
      toast.error(
        `${t("El archivo es muy pesado. El tamaño máximo es de")} ${
          MAX_FILE_SIZE / 1000000
        } MB.`
      );
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(next.file);
    reader.onloadend = () => setFile({ base64: reader.result as string, name: next.name });
  };

  const handleAddCertification = (data: IAddUserCertificationInput) => {
    if (!certification) {
      toast.promise(
        addUserCertification({
          variables: {
            addUserCertificationInput: {
              ...data,
              base64Certification: file?.base64,
            },
          },
        }),
        {
          loading: t("Agregando certificación"),
          success: t("Certificación agregada"),
          error: t("Error al agregar certificación"),
        }
      );

      return;
    }

    toast.promise(
      updateUserCertification({
        variables: {
          updateUserCertificationInput: {
            ...data,
            base64Certification: file?.base64,
            id: certification?.id,
          },
        },
      }),
      {
        loading: t("Actualizando certificación"),
        success: t("Certificación actualizada"),
        error: t("Error al actualizar certificación"),
      }
    );
  };

  const certificationType = [
    // * iterate ECertificationType enum and add to certificationType array
    ...Object.keys(ECertificationType).map((key) => ({
      label: ECertificationType[key],
      value: ECertificationType[key],
    })),
  ];

  const certificationTypeOptions = [
    { label: t("Certificación"), value: ETypeCertification.CERTIFICATION },
    { label: t("Curso"), value: ETypeCertification.COURSE },
    { label: t("Acreditación"), value: ETypeCertification.ACREDITATION },
    { label: t("Credencial"), value: ETypeCertification.CREDENTIAL },
  ];

  const cancelForm = () => onClose?.();
  const submitLabel = certification ? t("Guardar") : t("Agregar");
  const isDrawerMode = Boolean(open);

  useLayoutEffect(() => {
    const publish = onFooterChangeRef.current;
    if (isDrawerMode || !hideFooter || !publish) return;

    publish(
      <ModalButtons
        compact
        className="!pt-0"
        onCancel={cancelForm}
        loading={addCertLoading || updateLoading}
        submitLabel={submitLabel}
        cancelLabel={t("Cancelar")}
        submitButtonProps={{ form: formId, type: "submit" }}
      />
    );
  }, [
    addCertLoading,
    cancelForm,
    formId,
    hideFooter,
    isDrawerMode,
    submitLabel,
    t,
    updateLoading,
  ]);

  useEffect(() => {
    return () => {
      onFooterChangeRef.current?.(null);
    };
  }, []);

  const headerEl = (
    <div className="mb-4">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
        {t(certification ? "Editar certificación" : "Agregar certificaciones")}
      </h2>
      <p className="text-sm text-slate-500 dark:text-zinc-400">
        {t(
          "Agrega una certificación para que los usuarios puedan ver tu progreso en tu perfil"
        )}
      </p>
    </div>
  );

  const fields = (
    <div className="flex flex-wrap">
          {/* Nombre de la certificación */}
          <div className="mb-4 w-full md:w-full">
            <Controller
              name="name"
              control={control}
              rules={{ required: true, maxLength: 100 }}
              render={({ field }) => (
                <Input
                  label={t("Nombre de la certificación")}
                  leftIcon={<Award className="h-4 w-4" />}
                  maxLength={100}
                  {...field}
                  placeholder={t("CERTIFICATION_PLACEHOLDER")}
                  error={
                    errors.name
                      ? t("Ingresa el nombre de la certificación")
                      : undefined
                  }
                />
              )}
            />
          </div>

          {/* Fecha expiración + Fecha emisión */}
          <div className="flex w-full gap-2">
            <div className="mb-4 w-full md:w-1/2">
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-zinc-200">
                {t("Fecha expiración")}
              </label>
              <Controller
                name="expiracyDate"
                control={control}
                defaultValue={
                  certification?.expiracyDate
                    ? moment(certification.expiracyDate).toDate()
                    : undefined
                }
                render={({ field }) => (
                  <DatePickerV2
                    value={field.value ?? null}
                    onChange={field.onChange}
                    placeholder={t("Solo si aplica")}
                  />
                )}
              />
            </div>
            <div className="mb-4 w-full md:w-1/2">
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-zinc-200">
                {t("Fecha emisión")}
              </label>
              <Controller
                name="emitDate"
                control={control}
                defaultValue={
                  certification?.emitDate
                    ? moment(certification.emitDate).toDate()
                    : undefined
                }
                render={({ field }) => (
                  <DatePickerV2
                    value={field.value ?? null}
                    onChange={field.onChange}
                    placeholder={t("Solo si aplica")}
                  />
                )}
              />
            </div>
          </div>

          {/* Tipo + Origen */}
          <div className="flex w-full gap-2">
            <div className="mb-4 w-full md:w-1/2">
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-zinc-200">
                {t("Tipo")}
              </label>
              <Controller
                name="certificationType"
                control={control}
                rules={{ required: true }}
                defaultValue={ETypeCertification.CERTIFICATION}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder={t("Seleccione una opción")} />
                    </SelectTrigger>
                    <SelectContent>
                      {certificationTypeOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors && errors.certificationUrl?.type === "isRequired" && (
                <small className="w-full text-red-500">
                  {t(
                    "Asegurate de agregar una url con tu certificación o subir un archivo"
                  )}
                </small>
              )}
              {errors && errors.certificationUrl?.type === "url" && (
                <small className="w-full text-red-500">
                  {t("Asegurate de colocar una URL válida")}
                </small>
              )}
            </div>
            <div className="mb-4 w-full md:w-1/2">
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-zinc-200">
                {t("Origen de la certificación")}
              </label>
              <Controller
                name="type"
                control={control}
                rules={{ required: true }}
                defaultValue={ECertificationType.OTHER}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder={t("Seleccione una opción")} />
                    </SelectTrigger>
                    <SelectContent>
                      {certificationType.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          {/* Certificación */}
          <div className="mb-4 w-full">
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-zinc-200">
              {t("Certificación")}
            </label>
            <FileUploaderV2
              value={certImage}
              onChange={handleImageChange}
              accept={ALLOWED_EXTENSIONS}
              title={t("Arrastra y suelta tu certificación")}
              hint={t("PDF o imágenes · hasta 50MB")}
              aria-label={t("Subir certificación")}
            />
          </div>

        </div>
  );

  // Drawer mode: own the v2 Drawer; action buttons go in its fixed footer.
  if (open) {
    return typeof document !== "undefined"
      ? createPortal(
          <Drawer
            open
            onClose={cancelForm}
            side="right"
            size="lg"
            footer={
              <ModalButtons
                onCancel={cancelForm}
                loading={addCertLoading || updateLoading}
                submitLabel={submitLabel}
                cancelLabel={t("Cancelar")}
                onSubmit={() => handleSubmit(handleAddCertification)()}
              />
            }
          >
            {headerEl}
            <form
              id={formId}
              onSubmit={handleSubmit(handleAddCertification)}
              className="w-full"
            >
              {fields}
            </form>
          </Drawer>,
          document.body
        )
      : null;
  }

  // Legacy mode (rendered inside a legacy modal/drawer by BannerInfo / RightContent).
  return (
    <div className="p-3 md:p-6">
      {headerEl}
      <form
        id={formId}
        onSubmit={handleSubmit(handleAddCertification)}
        className="w-full"
      >
        {fields}
        {!hideFooter ? (
          <div className="mt-3 w-full">
            <ModalButtons
              onCancel={cancelForm}
              loading={addCertLoading || updateLoading}
              submitLabel={submitLabel}
              cancelLabel={t("Cancelar")}
            />
          </div>
        ) : null}
      </form>
    </div>
  );
};

export default CertificationForm;
